import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Button,
  Alert,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import { Memory, Delete, Warning } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../config/api';

interface MemoryStats {
  totalFiles: number;
  totalSize: number;
  totalSizeMB: number;
  deviceCount: number;
  averageFileSizeKB: number;
}

interface MemoryMonitorProps {
  onCleanup?: () => void;
}

const MemoryMonitor: React.FC<MemoryMonitorProps> = ({ onCleanup }) => {
  const { t } = useTranslation();
  const [memoryStats, setMemoryStats] = useState<MemoryStats | null>(null);
  const [platformMemory, setPlatformMemory] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<string | null>(null);

  // 获取平台相关的内存使用（更准确）
  const getPlatformMemory = (): number => {
    try {
      // 通过监控平台相关的资源来估算内存使用
      let platformMemory = 0;
      
      // 1. 计算blob URLs数量（截图相关）
      const images = document.querySelectorAll('img');
      let blobCount = 0;
      images.forEach(img => {
        if (img.src.startsWith('blob:')) {
          blobCount++;
        }
      });
      
      // 2. 估算每个blob URL的内存使用（假设每个截图约1-2MB）
      const blobMemory = blobCount * 1.5; // 每个blob约1.5MB
      
      // 3. 计算canvas元素
      const canvases = document.querySelectorAll('canvas');
      const canvasMemory = canvases.length * 0.5; // 每个canvas约0.5MB
      
      // 4. 计算video/audio元素
      const mediaElements = document.querySelectorAll('video, audio');
      const mediaMemory = mediaElements.length * 0.3; // 每个媒体元素约0.3MB
      
      // 5. 基础页面内存（React组件、状态等）
      const baseMemory = 5; // 基础页面约5MB
      
      platformMemory = blobMemory + canvasMemory + mediaMemory + baseMemory;
      
      return Math.round(platformMemory);
    } catch (e) {
      console.log('无法计算平台内存使用:', e);
      return 0;
    }
  };

  // 获取截图统计信息
  const fetchMemoryStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_ENDPOINTS.SCREEN_MONITOR}/stats`);
      if (response.ok) {
        const stats = await response.json();
        setMemoryStats(stats);
      } else {
        setError('获取内存统计失败');
      }
    } catch (err) {
      setError('获取内存统计失败');
    } finally {
      setLoading(false);
    }
  };

  // 清理截图文件
  const handleCleanup = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_ENDPOINTS.SCREEN_MONITOR}/cleanup-screenshots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setMemoryStats(result.stats);
        
        // 清理平台内存
        cleanupPlatformMemory();
        
        if (onCleanup) {
          onCleanup();
        }
      } else {
        setError('清理截图失败');
      }
    } catch (err) {
      setError('清理截图失败');
    } finally {
      setLoading(false);
    }
  };

  // 清理平台内存
  const cleanupPlatformMemory = () => {
    console.log('强制清理内存按钮被点击！');
    const beforeMemory = getPlatformMemory();
    setCleanupResult(`开始清理... 当前平台内存: ${beforeMemory}MB`);
    setCleaning(true);
    
    // 使用setTimeout确保状态更新完成后再执行清理
    setTimeout(() => {
      try {
        console.log('开始强制清理浏览器内存...');
      
      // 1. 清理所有图片缓存和blob URLs
      const images = document.querySelectorAll('img');
      let cleanedImages = 0;
      images.forEach(img => {
        if (img.src.startsWith('blob:')) {
          URL.revokeObjectURL(img.src);
          img.src = '';
          cleanedImages++;
        }
      });
      console.log(`清理了 ${cleanedImages} 个图片blob URLs`);
      
      // 2. 清理所有canvas元素
      const canvases = document.querySelectorAll('canvas');
      canvases.forEach(canvas => {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      });
      console.log(`清理了 ${canvases.length} 个canvas元素`);
      
      // 3. 清理所有video和audio元素
      const mediaElements = document.querySelectorAll('video, audio');
      mediaElements.forEach(element => {
        const mediaElement = element as HTMLVideoElement | HTMLAudioElement;
        mediaElement.src = '';
        mediaElement.load();
      });
      console.log(`清理了 ${mediaElements.length} 个媒体元素`);
      
      // 4. 清理所有iframe元素
      const iframes = document.querySelectorAll('iframe');
      iframes.forEach(iframe => {
        if (iframe.src.startsWith('blob:')) {
          URL.revokeObjectURL(iframe.src);
          iframe.src = '';
        }
      });
      console.log(`清理了 ${iframes.length} 个iframe元素`);
      
      // 5. 清理localStorage中的大对象
      try {
        const keys = Object.keys(localStorage);
        let cleanedStorage = 0;
        keys.forEach(key => {
          const value = localStorage.getItem(key);
          if (value && value.length > 5000) { // 大于5KB的项
            localStorage.removeItem(key);
            cleanedStorage++;
          }
        });
        console.log(`清理了 ${cleanedStorage} 个localStorage大对象`);
      } catch (e) {
        console.log('localStorage清理失败:', e);
      }
      
      // 6. 清理sessionStorage
      try {
        sessionStorage.clear();
        console.log('清理了sessionStorage');
      } catch (e) {
        console.log('sessionStorage清理失败:', e);
      }
      
      // 7. 强制垃圾回收（如果可用）
      if ((window as any).gc) {
        (window as any).gc();
        console.log('执行了垃圾回收');
      }
      
      // 8. 创建临时对象然后清理，强制垃圾回收
      try {
        const tempArray = [];
        for (let i = 0; i < 1000; i++) {
          tempArray.push(new Array(100).fill(0));
        }
        tempArray.length = 0;
      } catch (e) {
        console.log('临时对象清理失败:', e);
      }
      
      // 9. 更安全的清理策略
      // 清理所有定时器（只清理我们知道的）
      try {
        // 清理可能存在的定时器
        const timeouts = [];
        for (let i = 1; i < 1000; i++) {
          try {
            clearTimeout(i);
            clearInterval(i);
          } catch (e) {
            // 忽略不存在的定时器
          }
        }
      } catch (e) {
        console.log('定时器清理失败:', e);
      }
      
      // 9. 更新平台内存显示
      setTimeout(() => {
        const newMemory = getPlatformMemory();
        setPlatformMemory(newMemory);
        setCleaning(false);
        
        // 计算内存变化
        const memoryChange = beforeMemory - newMemory;
        const changeText = memoryChange > 0 ? `释放了 ${memoryChange}MB` : memoryChange < 0 ? `增加了 ${Math.abs(memoryChange)}MB` : '无变化';
        
        setCleanupResult(`清理完成！${changeText} (${beforeMemory}MB → ${newMemory}MB)`);
        console.log(`强制平台内存清理完成，当前使用: ${newMemory}MB`);
        
        // 如果内存还是很高，建议刷新页面
        if (newMemory > 30) {
          console.warn(`平台内存仍然较高: ${newMemory}MB，建议刷新页面`);
          setCleanupResult(`清理完成！${changeText} (${beforeMemory}MB → ${newMemory}MB) - 平台内存仍然较高，建议刷新页面`);
        }
        
        // 5秒后清除结果提示
        setTimeout(() => {
          setCleanupResult(null);
        }, 5000);
      }, 2000);
      
        console.log('强制浏览器内存清理完成');
      } catch (error) {
        console.error('强制浏览器内存清理失败:', error);
        setCleaning(false);
        setCleanupResult('清理失败，请重试');
        setTimeout(() => {
          setCleanupResult(null);
        }, 3000);
      }
    }, 100); // 100ms延迟确保状态更新完成
  };

  // 更新平台内存使用情况
  useEffect(() => {
    const updatePlatformMemory = () => {
      const memory = getPlatformMemory();
      setPlatformMemory(memory);
      
      // 如果平台内存使用过高，记录日志
      if (memory > 30) {
        console.log(`平台内存使用较高 (${memory}MB)，建议清理截图`);
      }
    };

    updatePlatformMemory();
    const interval = setInterval(updatePlatformMemory, 5000); // 每5秒更新一次

    return () => clearInterval(interval);
  }, []);

  // 初始加载和定期更新
  useEffect(() => {
    fetchMemoryStats();
    const interval = setInterval(fetchMemoryStats, 30000); // 每30秒更新一次

    return () => clearInterval(interval);
  }, []);

  const getMemoryLevel = (sizeMB: number): 'low' | 'medium' | 'high' => {
    if (sizeMB < 10) return 'low';
    if (sizeMB < 20) return 'medium';
    return 'high';
  };

  const getMemoryColor = (level: 'low' | 'medium' | 'high'): 'success' | 'warning' | 'error' => {
    switch (level) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
    }
  };

  const memoryLevel = memoryStats ? getMemoryLevel(memoryStats.totalSizeMB) : 'low';
  const memoryColor = getMemoryColor(memoryLevel);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" display="flex" alignItems="center" gap={1}>
            <Memory />
            {t('memoryMonitor.title')}
          </Typography>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<Delete />}
            onClick={handleCleanup}
            disabled={loading}
            size="small"
          >
            {t('memoryMonitor.cleanScreenshots')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Memory />}
            onClick={cleanupPlatformMemory}
            disabled={loading || cleaning}
            size="small"
            sx={{ ml: 1 }}
          >
            {cleaning ? t('memoryMonitor.cleaning') : t('memoryMonitor.forceCleanMemory')}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {cleanupResult && (
          <Alert 
            severity={cleanupResult.includes('失败') ? 'error' : cleanupResult.includes('释放了') ? 'success' : 'info'} 
            sx={{ mb: 2 }}
          >
            {cleanupResult}
          </Alert>
        )}

        {memoryStats && (
          <Box>
            {/* 截图文件统计 */}
            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  {t('memoryMonitor.screenshotFileSize')}
                </Typography>
                <Chip
                  label={`${memoryStats.totalSizeMB}MB`}
                  color={memoryColor}
                  size="small"
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min((memoryStats.totalSizeMB / 30) * 100, 100)}
                color={memoryColor}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {t('memoryMonitor.target')}: {t('memoryMonitor.targetBelow')}
              </Typography>
            </Box>

            {/* 详细统计信息 */}
            <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(120px, 1fr))" gap={2} mb={2}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {memoryStats.totalFiles}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('memoryMonitor.fileCount')}
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {memoryStats.deviceCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('memoryMonitor.deviceCount')}
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {memoryStats.averageFileSizeKB}KB
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('memoryMonitor.averageFileSize')}
                </Typography>
              </Box>
            </Box>

            {/* 平台内存使用情况 */}
            {platformMemory !== null && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    {t('memoryMonitor.platformMemoryUsage')}
                  </Typography>
                  <Chip
                    label={`${platformMemory}MB`}
                    color={platformMemory > 30 ? 'error' : platformMemory > 20 ? 'warning' : 'success'}
                    size="small"
                  />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min((platformMemory / 50) * 100, 100)}
                  color={platformMemory > 30 ? 'error' : platformMemory > 20 ? 'warning' : 'success'}
                  sx={{ height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {t('memoryMonitor.platformMemoryDescription')}
                </Typography>
              </Box>
            )}

            {/* 内存警告 */}
            {memoryLevel === 'high' && (
              <Alert severity="warning" sx={{ mt: 2 }} icon={<Warning />}>
                截图文件占用内存较高，建议清理旧文件以释放空间
              </Alert>
            )}

            {platformMemory && platformMemory > 30 && (
              <Alert severity="error" sx={{ mt: 2 }} icon={<Warning />}>
                {t('memoryMonitor.memoryHighWarning')}
              </Alert>
            )}
            {platformMemory && platformMemory > 20 && platformMemory <= 30 && (
              <Alert severity="warning" sx={{ mt: 2 }} icon={<Warning />}>
                {t('memoryMonitor.memoryWarning')}
              </Alert>
            )}
          </Box>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress size={24} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MemoryMonitor;
